import { useEffect, useState } from "react";
import { PauseCircle, Mail } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export function SuspendDialog({ tenant, open, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [mode, setMode] = useState("amministrativa");
  const [message, setMessage] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  if (!tenant) return null;
  const submit = async () => {
    setBusy(true);
    try {
      await api.put(`/superadmin/tenants/${tenant.id}/status`, { status: "sospeso", reason, mode, message, confirm });
      toast.success(`Azienda sospesa (${mode}) — dati conservati`);
      onDone(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Operazione non riuscita"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="suspend-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><PauseCircle className="h-5 w-5 text-amber-500" /> Sospendi «{tenant.name}»</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="space-y-1"><Label>Motivazione (obbligatoria, registrata nel log)</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="suspend-reason" placeholder="Es. insoluto fattura 05/2026" /></div>
          <div className="space-y-2"><Label>Modalità</Label>
            <label className="flex items-start gap-2 cursor-pointer rounded-lg border border-slate-200 p-2"><input type="radio" className="mt-1" checked={mode === "amministrativa"} onChange={() => setMode("amministrativa")} data-testid="suspend-mode-admin" />
              <span><b>Amministrativa</b><br /><span className="text-xs text-slate-500">Gli utenti non accedono; i messaggi WhatsApp in arrivo vengono ricevuti e conservati; nessuna risposta automatica, automazione o campagna.</span></span></label>
            <label className="flex items-start gap-2 cursor-pointer rounded-lg border border-slate-200 p-2"><input type="radio" className="mt-1" checked={mode === "completa"} onChange={() => setMode("completa")} data-testid="suspend-mode-full" />
              <span><b>Completa</b><br /><span className="text-xs text-slate-500">Come sopra, ma i messaggi in arrivo non vengono elaborati dall'applicazione (restano solo gli eventi webhook grezzi secondo retention). Le richieste STOP vengono comunque registrate. Nessuna modifica a token, webhook o numeri Meta.</span></span></label>
          </div>
          {mode === "completa" && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-2 space-y-1">
              <p className="text-xs text-amber-800">Conseguenze: chat, campagne, automazioni e IA del cliente sono ferme; nessun dato viene cancellato; riattivazione possibile in qualsiasi momento. Digita l'identificativo <b>{tenant.id}</b> per confermare.</p>
              <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="suspend-confirm" placeholder={tenant.id} />
            </div>
          )}
          <div className="space-y-1"><Label>Messaggio mostrato agli utenti dell'azienda (facoltativo)</Label>
            <Textarea value={message} onChange={(e) => setMessage(e.target.value)} data-testid="suspend-message" rows={3} placeholder="Es. Il servizio è sospeso per verifiche amministrative. Per riattivarlo contatta Studio Nouvelle." /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="suspend-cancel">Annulla</Button>
          <Button disabled={busy || !reason.trim() || (mode === "completa" && confirm !== tenant.id)} onClick={submit} className="bg-amber-500 hover:bg-amber-600 text-slate-900" data-testid="suspend-submit">Sospendi azienda</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function SupportSettings() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/superadmin/platform-settings").then((r) => setS(r.data)).catch(() => setS({})); }, []);
  if (!s) return null;
  const save = async () => {
    try { const r = await api.put("/superadmin/platform-settings", { support_email: s.support_email || "", support_phone: s.support_phone || "", suspended_default_message: s.suspended_default_message || "" }); setS(r.data); toast.success("Riferimenti assistenza salvati"); }
    catch (e) { toast.error(e.response?.data?.detail || "Salvataggio non riuscito"); }
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-2" data-testid="sa-support-settings">
      <p className="text-sm font-semibold text-slate-800 flex items-center gap-2"><Mail className="h-4 w-4 text-[#10B981]" /> Assistenza Studio Nouvelle (mostrata nella pagina «Servizio sospeso»)</p>
      <div className="grid sm:grid-cols-2 gap-2">
        <Input value={s.support_email || ""} onChange={(e) => setS({ ...s, support_email: e.target.value })} placeholder="assistenza@studionouvelle.it" data-testid="sa-support-email" className="h-8 text-sm" />
        <Input value={s.support_phone || ""} onChange={(e) => setS({ ...s, support_phone: e.target.value })} placeholder="Telefono assistenza (facoltativo)" data-testid="sa-support-phone" className="h-8 text-sm" />
      </div>
      <Textarea value={s.suspended_default_message || ""} onChange={(e) => setS({ ...s, suspended_default_message: e.target.value })} rows={2} placeholder="Messaggio predefinito per le aziende sospese (usato se non ne è impostato uno specifico)" data-testid="sa-support-default-message" className="text-sm" />
      <Button size="sm" onClick={save} className="h-8 bg-[#2F7D32] text-white" data-testid="sa-support-save">Salva</Button>
    </div>
  );
}
